import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { FormService } from 'src/app/shared/services/form.service';
import { WorksheetResponse } from 'src/app/features/worksheet/models/worksheet-response.modal';
import { MatDialog } from '@angular/material/dialog';
import { NotesComponent } from 'src/app/shared/components/notes/notes.component';
import { InstructionsComponent } from 'src/app/shared/components/instructions/instructions.component';
import { ProjectDetailsComponent } from 'src/app/shared/components/project-details/project-details.component';
import { MatTableDataSource } from '@angular/material/table';
import { AssignToLabtechComponent } from 'src/app/shared/components/assign-to-labtech/assign-to-labtech.component';
import { UserService } from 'src/app/shared/services/user.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { INotes } from 'src/app/features/models/inotes.modal';
import { SharedService } from 'src/app/features/service/shared.service';
import { NativeSoilService } from 'src/app/features/worksheet/services/native-soil/native-soil.service';
import { MoistureWorkSheetPatchDto, MultipleMoistureResultsDto, NativeSoilType } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { DialogService } from 'src/app/shared/services/dialog.service';

@Component({
    selector: 'app-moisture-conditioning-worksheet',
    templateUrl: './moisture-conditioning-worksheet.component.html',
    styleUrls: ['./moisture-conditioning-worksheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MoistureConditioningWorksheetComponent {
@Output() assigned = new EventEmitter<boolean>();
  @Output() rowDeletionSuccess = new EventEmitter<void>();
  selection = new SelectionModel<NativeSoilType>(true,[]);
  private readonly defaultDrySoil = 1200;
  
  hasUnsavedChanges = false;
  projectId: string | null = null;
  private readonly destroyed$ = new Subject<boolean>();
  public dataSource = new MatTableDataSource<NativeSoilType>();
  public worksheets: NativeSoilType[];
  public worksheetRecords: WorksheetResponse | null = null;
  private originalWorksheetData: any[] = [];
  public note: INotes | undefined;
  userRoles: string[];
  daysLeft: number = 0;
  noteText: string | undefined = '';
  columns = [
    { key: 'select', displayName: '' },
    { key: 'sampleId', displayName: 'Sample Id' },
    { key: 'compactionEffort', displayName: 'Compaction Effort' },
    { key: 'depth', displayName: 'Depth' },
    { key: 'testNo', displayName: 'Test #' },
    { key: 'drySoil', displayName: 'Amount of Dry Soil' },
    { key: 'moistureContent', displayName: 'Moisture Content' },
    { key: 'addWater', displayName: 'Add Water' },
    { key: 'addMoisture', displayName: 'Add' },
    { key: 'proctorDryDensity', displayName: 'Proctor Dry Density' },
    { key: 'geothermSoilDescription', displayName: 'Geotherm Soil Description' },
    { key: 'assignedTo', displayName: 'Assigned To' },
    { key: 'status', displayName: 'Status' },
    { key: 'action', displayName: '' }
  ];
  displayedColumns = this.columns.map(c => c.key);
  rowData: NativeSoilType[];
  isInstructionsConfirmed: boolean;

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private worksheetService: WorksheetService,
    private formService: FormService,
    private sharedService: SharedService,
    public dialog: MatDialog,
    public userService: UserService,
    private snackBar: MatSnackBar,
    private nativeSoilService: NativeSoilService,
    private dialogService: DialogService
  ) { }
  
  canDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges) {
      return new Observable<boolean>(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'Moisture-Conditioning-Worksheet',
      data: {
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to leave without saving?',
        cancelText: 'Stay',
        confirmText: 'Leave'
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    return dialogRef.afterClosed().pipe(
      map(result => result === 'YES')
    );
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    this.fetchWorksheet(this.projectId ?? '');
    this.fetchWorksheetNotes(this.projectId ?? '');
    this.fetchWorksheetDetails(this.projectId ?? '');
    this.userAccountAndRoleAndFilterDisplayColumns();
  }

  addMultipleMoisture(element: NativeSoilType): void {
      if (this.hasEnoughSoilForTest(element)) {
          this.pushMoistureTest(element, this.defaultDrySoil);
          this.refreshAfterMoistureAdd();
          return;
      }

      const remaining = this.getRemainingSoil(element) ?? 0;
      this.confirmNotEnoughSoil(
          `Sample <b>${element.sampleId ?? ''}</b> has only <b>${remaining} g</b> of soil remaining.`,
          `Proceed with remaining soil (${remaining} g)`,
          () => {
              this.pushMoistureTest(element, this.getRemainingSoil(element) ?? 0);
              this.refreshAfterMoistureAdd();
          },
          () => {
              this.pushMoistureTest(element, this.defaultDrySoil);
              this.refreshAfterMoistureAdd();
          }
      );
  }

  // Total amount of dry soil across every moisture test of every row of the sample (the rows between bold borders)
  private getSampleDrySoilTotal(nativeSoilSampleId: string | undefined): number {
    return this.dataSource.data
      .filter(row => row.nativeSoilSampleId === nativeSoilSampleId)
      .reduce((total, row) =>
        total + (row.multipleMoistureResults ?? []).reduce((sum, result) => sum + Number(result.drySoil ?? 0), 0), 0);
  }

  // Weight received minus the dry soil already used by the sample (never below 0); null when no weight was received
  private getRemainingSoil(row: NativeSoilType): number | null {
    const weightReceived = Number(row.weightReceived ?? 0);
    if (weightReceived <= 0) { return null; }

    return Math.max(weightReceived - this.getSampleDrySoilTotal(row.nativeSoilSampleId), 0);
  }

  private hasEnoughSoilForTest(row: NativeSoilType): boolean {
    const remaining = this.getRemainingSoil(row);
    return remaining === null || remaining >= this.defaultDrySoil;
  }

  private pushMoistureTest(row: NativeSoilType, drySoil: number): void {
    if (!row.multipleMoistureResults) {
      row.multipleMoistureResults = [];
    }
    row.multipleMoistureResults.push({
      id: '',
      testNumber: 0,
      drySoil,
      moistureContent: 0
    });
    this.renumberMultipleMoisture(row);
    this.updateCalculations(row);
  }

  private refreshAfterMoistureAdd(): void {
    this.hasUnsavedChanges = true;
    this.dataSource.data = [...this.dataSource.data];
  }

  // Cancel adds nothing, proceed uses the remaining soil, and the alternate button adds the full default amount anyway
  private confirmNotEnoughSoil(content: string, proceedText: string, onProceed: () => void, onProceedAnyway: () => void): void {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'Moisture-Conditioning-Worksheet',
      data: {
        title: 'Not Enough Soil',
        content,
        cancelText: 'Cancel',
        alternateText: `Proceed with ${this.defaultDrySoil} g`,
        continueText: proceedText
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    })
    .afterClosed()
    .subscribe(result => {
      if (result === 'YES') {
        onProceed();
      } else if (result === 'ALTERNATE') {
        onProceedAnyway();
      }
    });
  }

  private renumberMultipleMoisture(element: NativeSoilType) {
    if (!element.multipleMoistureResults) {
      element.multipleMoistureResults = [];
      return;
    }

    element.multipleMoistureResults.forEach((t, i) => {
      t.testNumber = i + 1;
    });
  }

  viewSubmissionForm(): void {
    const params = {
      f: 'w',
    };
    const navigationExtras: NavigationExtras = {
      queryParams: params,
      state: {
        isShipmentReceived: this.worksheetRecords?.project?.storageCompartment
      }
    };

    this.formService.updateData(null);
    this.router.navigate([`/review/form/nativesoil/${this.projectId}`], navigationExtras);
  }

  userAccountAndRoleAndFilterDisplayColumns() {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
        if (this.isLabtech()) {
          this.displayedColumns = this.displayedColumns.filter(
            x => x !== 'testNo' && x !== 'addMoisture'
          );
        }
      }
    });
  }

  isLabtech(): boolean {
    return this.userRoles?.includes('Labtech');
  }

  isAdmin(): boolean {
    return this.userRoles?.includes('Admin');
  }

  toggleSelection(row: NativeSoilType) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  isIndeterminate() {
    return this.selection.selected.length > 0 && !this.isAllSelected();
  }

  isMultipleSelected(row: any): boolean {
    return this.selection.selected.length >= 1 && this.selection.selected.includes(row);
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  printTargetWeights() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/native-soil-print'],
        { queryParams: { projectId: this.projectId } }
      )
    );

    window.open(url, '_blank');
  }

  isLastRowOfEffort(row: NativeSoilType, index: number): boolean {
    const nextRow = this.dataSource.data[index + 1];
    if (!nextRow) return false;

    return (
      nextRow.nativeSoilSampleId === row.nativeSoilSampleId &&
      nextRow.compactionEffort !== row.compactionEffort
    );
  }

  isFirstRowOfSample(row: NativeSoilType, index: number): boolean {
    if (index === 0) {
      return true;
    }

    return this.dataSource.data[index - 1].nativeSoilSampleId !== row.nativeSoilSampleId;
  }

  copyDescriptionToGroup(row: NativeSoilType) {
    this.dataSource.data
        .filter(x => x.nativeSoilSampleId === row.nativeSoilSampleId)
        .forEach((x: any) => {
            x.geothermSoilDescription = row.geothermSoilDescription;
            if (!this.canCompleteRow(x)) {
              x.status = false;
            }
        });

    this.hasUnsavedChanges = true;
  }

  // The description input only renders on the first row of each sample, so jump to the next rendered one
  focusNextDescription(event: Event): void {
    event.preventDefault();
    const inputs = Array.from(
      document.querySelectorAll('[data-type="geothermSoilDescription"]')
    ) as HTMLInputElement[];
    const nextInput = inputs[inputs.indexOf(event.target as HTMLInputElement) + 1];

    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  }

  isLastRowOfSample(row: NativeSoilType, index: number): boolean {
    const currentSampleId = row.nativeSoilSampleId;
    const nextRow = this.dataSource.data[index + 1];

    if (!nextRow) return false;
    return nextRow.nativeSoilSampleId !== currentSampleId;
  }

  onFocusClearZero(obj: any, field: string) {
    if (obj[field] === 0) {
      obj[field] = null;
    }
  }

  onBlurRestoreZero(obj: any, field: string) {
    if (obj[field] === null || obj[field] === '') {
      obj[field] = 0;
    }
  }

  preventNumberInputChange(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  asReceivedWorksheet(): void {
    this.navigateToWorksheet('as-received-worksheet');
  }

  moistureConditioningWorksheet(): void {
    this.navigateToWorksheet('moisture-conditioning-worksheet');
  }

  moistureConditioningHistory(): void {
    this.navigateToWorksheet('moisture-conditioning-history');
  }

  singleProctorWorksheet(): void {
    this.navigateToWorksheet('single-proctor-worksheet');
  }

  compactionWorksheet(): void {
    this.navigateToWorksheet('compaction-worksheet');
  }

  thermalResistivityWorksheet(): void {
    this.navigateToWorksheet('native-soil-worksheet');
  }

  private navigateToWorksheet(route: string): void {
    this.canDeactivate().subscribe((canLeave: boolean) => {
      if (canLeave) {
        this.openWorksheet(route);
      }
    });
  }

  openWorksheet(route: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [route],
        {
          queryParams: {
            projectId: this.projectId
          }
        }
      )
    );

    window.location.href = url;
  }

  completeRow(row: any, event: any): void {
    row.status = event.checked;
    this.saveTable();
  }

  focusNextRow(event: Event, currentRowId: string, fieldType: string, currentTestIndex: number): void { event.preventDefault();
    const currentIndex = this.dataSource.data.findIndex(x => x.id === currentRowId);
    if (currentIndex === -1) { return; }

    const currentRow = this.dataSource.data[currentIndex];
    const nextTestIndex = currentTestIndex + 1;

    // First, move to the next moisture result in the SAME worksheet row
    if (currentRow.multipleMoistureResults && nextTestIndex < currentRow.multipleMoistureResults.length) {
      setTimeout(() => {
        const nextInput = document.querySelector(
          `[data-row-id="${currentRowId}"][data-type="${fieldType}"][data-test-index="${nextTestIndex}"]`
        ) as HTMLInputElement;

        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      });
      return;
    }

    // No more moisture results in this row.
    // Move to the first moisture result of the NEXT worksheet row.
    const nextRow = this.dataSource.data[currentIndex + 1];
    if (!nextRow) { return; }
    setTimeout(() => {
      const nextInput = document.querySelector(
        `[data-row-id="${nextRow.id}"][data-type="${fieldType}"][data-test-index="0"]`
      ) as HTMLInputElement;

      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    });
  }

  saveTable(): void {
    const payload: MoistureWorkSheetPatchDto[] = [];

    for (const currentRow of this.dataSource.data) {
      const originalRow = this.originalWorksheetData.find(original => original.id === currentRow.id);
      if (!originalRow) { continue; }

      const changes = this.getChangedWorksheetProperties(originalRow, currentRow);
      if (Object.keys(changes).length === 0) { continue; }

      payload.push({
        id: currentRow.id!,
        projectId: this.projectId!,
        ...changes
      });
    }

    if (payload.length === 0) {
      this.hasUnsavedChanges = false;
      this.snackBar.open('No changes to save', 'DONE', { duration: 3000, verticalPosition: 'top' });
      return;
    }

    this.worksheetService.saveMoistureConditioningWorksheetTable(payload).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;
        this.snackBar.open(
          'Saved successfully',
          'DONE',
          {
            duration: 3000,
            verticalPosition: 'top'
          }
        );

        this.ngOnInit();
      },
      error: () => {
        this.dialogService.open(
          messagesConfig.saveTableError
        );
      }
    });
  }

  private getChangedWorksheetProperties(
    original: NativeSoilType,
    current: NativeSoilType
  ): Partial<MoistureWorkSheetPatchDto> {

    const changes: Partial<MoistureWorkSheetPatchDto> = {};

    // Existing dry soil
    if (Number(original.drySoil ?? 0) !== Number(current.drySoil ?? 0)) {
      changes.drySoil = Number(current.drySoil ?? 0);
    }

    // Existing moisture content
    if (
      Number(original.moistureContent ?? 0) !==
      Number(current.moistureContent ?? 0)
    ) {
      changes.moistureContent = Number(current.moistureContent ?? 0);
    }

    // Status
    if (original.status !== current.status) { changes.status = Boolean(current.status) }

    if (original.geothermSoilDescription !== current.geothermSoilDescription) {
      changes.geothermSoilDescription = current.geothermSoilDescription;
    }

    // Multiple moisture conditioning results
    const resultChanges = this.getChangedMultipleMoistureResults(
      original.multipleMoistureResults ?? [],
      current.multipleMoistureResults ?? []
    );

    if (resultChanges.length > 0) {
      changes.multipleMoistureResults = resultChanges;
    }

    return changes;
  }

  private getChangedMultipleMoistureResults(
    originalResults: MultipleMoistureResultsDto[],
    currentResults: MultipleMoistureResultsDto[]
  ): MultipleMoistureResultsDto[] {

    const changes: MultipleMoistureResultsDto[] = [];

    // Added or modified results
    for (const current of currentResults) {

      // New result
      if (!current.id) {
        changes.push({
          id: null,
          testNumber: Number(current.testNumber ?? 0),
          drySoil: Number(current.drySoil ?? 0),
          moistureContent: Number(current.moistureContent ?? 0)
        });

        continue;
      }

      // Find existing result
      const original = originalResults.find(
        result => result.id === current.id
      );

      if (!original) {
        continue;
      }

      const resultChange: MultipleMoistureResultsDto = {
        id: current.id,
        testNumber: current.testNumber,
        drySoil: current.drySoil,
        moistureContent: current.moistureContent
      };

      let hasChanges = false;

      // Test number changed
      if (
        Number(original.testNumber ?? 0) !==
        Number(current.testNumber ?? 0)
      ) {
        resultChange.testNumber = Number(current.testNumber ?? 0);
        hasChanges = true;
      }

      // Dry soil changed
      if (
        Number(original.drySoil ?? 0) !==
        Number(current.drySoil ?? 0)
      ) {
        resultChange.drySoil = Number(current.drySoil ?? 0);
        hasChanges = true;
      }

      // Moisture content changed
      if (
        Number(original.moistureContent ?? 0) !==
        Number(current.moistureContent ?? 0)
      ) {
        resultChange.moistureContent =
          Number(current.moistureContent ?? 0);
        hasChanges = true;
      }

      if (hasChanges) {
        changes.push(resultChange);
      }
    }

    // Deleted results
    for (const original of originalResults) {

      if (!original.id) {
        continue;
      }

      const stillExists = currentResults.some(
        current => current.id === original.id
      );

      if (!stillExists) {
        changes.push({
          id: original.id,
          deleted: true
        } as unknown as MultipleMoistureResultsDto);
      }
    }

    return changes;
  }

  addMultipleMoistureToAll(): void {
    const rows = this.dataSource.data;
    if (!rows?.length) { return; }

    // Rows with enough soil get a full test now; the rest wait for the user's decision.
    // Totals include tests added earlier in this loop, so rows of the same sample share what is left.
    const shortRows: NativeSoilType[] = [];
    rows.forEach((sample: NativeSoilType) => {
      if (this.hasEnoughSoilForTest(sample)) {
        this.pushMoistureTest(sample, this.defaultDrySoil);
      } else {
        shortRows.push(sample);
      }
    });
    this.refreshAfterMoistureAdd();
    this.focusFirstNewDrySoil(rows);

    if (shortRows.length === 0) { return; }

    // Short rows grouped by sample: one row per compaction effort that still needs a test
    const shortRowsBySample = new Map<string | undefined, NativeSoilType[]>();
    shortRows.forEach(row => {
      shortRowsBySample.set(row.nativeSoilSampleId, [...(shortRowsBySample.get(row.nativeSoilSampleId) ?? []), row]);
    });

    const shortSamples = [...shortRowsBySample.values()]
      .map(sampleRows => `<b>${sampleRows[0].sampleId ?? ''}</b>: ${this.getRemainingSoil(sampleRows[0]) ?? 0} g remaining`)
      .join('<br>');
    this.confirmNotEnoughSoil(
      shortSamples,
      'Proceed with remaining soil',
      () => {
        // The remaining soil is split evenly across the sample's efforts; any leftover gram goes to the first one
        shortRowsBySample.forEach(sampleRows => {
          const remaining = this.getRemainingSoil(sampleRows[0]) ?? 0;
          const share = Math.floor(remaining / sampleRows.length);
          const leftover = remaining - share * sampleRows.length;
          sampleRows.forEach((row, index) => this.pushMoistureTest(row, share + (index === 0 ? leftover : 0)));
        });
        this.refreshAfterMoistureAdd();
      },
      () => {
        shortRows.forEach(row => this.pushMoistureTest(row, this.defaultDrySoil));
        this.refreshAfterMoistureAdd();
      }
    );
  }

  private focusFirstNewDrySoil(rows: NativeSoilType[]): void {
    setTimeout(() => {
      const firstRow = rows[0];

      if (
        !firstRow?.id ||
        !firstRow.multipleMoistureResults?.length
      ) {
        return;
      }

      const lastMoistureIndex =  firstRow.multipleMoistureResults.length - 1;

      const target = document.querySelector(
        `[data-row-id="${firstRow.id}"][data-type="drySoil"][data-test-index="${lastMoistureIndex}"]`
      ) as HTMLInputElement;

      target?.focus();
      target?.select();
    });
  }

  fetchWorksheetNotes(projectId: string): void {
    this.sharedService.fetchWSNotes(projectId);
    this.sharedService.notes$.subscribe((data: any) => {
      this.note = data;
    });
    this.sharedService.notesText$.subscribe((data: string | undefined) => {
      this.noteText = data;
    });
  }

  fetchWorksheetDetails(projectId: string): void {
    this.formService.fetchFormData(projectId, 'nativesoil').subscribe({
      next: (result) => {
        const project = result?.project;
        const company = result?.company;
        const comments = result?.comments;
        this.worksheetRecords = {
          project,
          company,
          comments
        };
        if(this.worksheetRecords.project.dueDate) {
          const dueDate = new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(this.worksheetRecords.project.dueDate));
          this.daysLeft = this.findDateDifference(new Date(), dueDate);
        }
        this.isInstructionsConfirmed = this.worksheetRecords.project?.instructionsConfirmed ?? false;
      },
    });
  }

  updateCalculations(element: any, pageLoad: boolean = false): void {
    if (!pageLoad) { this.hasUnsavedChanges = true; }

    // Recalculate all moisture test values for this row
    (element.multipleMoistureResults ?? []).forEach((multipleMoisture: any) => {
      multipleMoisture.addWater = this.calculateAddWaterContent(element, multipleMoisture.moistureContent, multipleMoisture.drySoil);
    });

    if (!this.canCompleteRow(element)) {
      element.status = false;
    }
  }

  canCompleteRow(row: any): boolean {
    const results: MultipleMoistureResultsDto[] = row.multipleMoistureResults ?? [];
    const hasValidResults = results.length > 0 && results.every(result =>
      Number(result.drySoil ?? 0) > 0 && Number(result.moistureContent ?? 0) > 0
    );

    return hasValidResults && !!row.geothermSoilDescription?.trim();
  }
  
  calculateAddWaterContent(element: any, moistureContent: number, drySoil: number): number {
      const drySoilWeight = Number(drySoil ?? 0);
      const moisture = Number(moistureContent ?? 0);

      if (!drySoilWeight || !moisture) {
          return 0;
      }
      return +(drySoilWeight * moisture / 100).toFixed(0);
  }

  removeMultipleMoisture(element: NativeSoilType, index: number) {
    if (!element.multipleMoistureResults) return;

    // If only one moisture test exists, don't remove the row.
    // Just reset its values.
    if (element.multipleMoistureResults.length === 1) {
      const last = element.multipleMoistureResults[0];

      last.drySoil = 0;
      last.moistureContent = 0;

      this.updateCalculations(element);
      this.dataSource.data = [...this.dataSource.data];

      return;
    }

    // Remove the selected moisture test
    element.multipleMoistureResults.splice(index, 1);

    // Renumber remaining tests
    this.renumberMultipleMoisture(element);
    this.updateCalculations(element);
    this.dataSource.data = [...this.dataSource.data];
  }

  getTestStatusClass(test: any): string {
    return 'status-red';
  }

  openViewNotesDialog(): void {
    const messageObject = {
      projectId: this.projectId,
      notes: this.note,
    };
    this.dialog
      .open(NotesComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetNotes(this.projectId ?? '');
      });
  }

  openViewInstructionsDialog(): void {
    if (!this.worksheetRecords?.comments) return;

    this.dialog.open(InstructionsComponent, {
      panelClass: 'app-dialog',
      data: this.worksheetRecords.comments,
      disableClose: true,
      width: '50%',
    });
  }

  openProjectDetailsComponent(title: string, fieldName: string): void {
    if (!this.worksheetRecords?.project) return;
    const project = this.worksheetRecords.project;
    const messageObject = {
      projectId: this.projectId,
      title,
      fieldName,
      ovenDate: project.ovenDate,
      wetTestData: project.wetTestData,
      dryTestData: project.dryTestData,
    };
    this.dialog
      .open(ProjectDetailsComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetDetails(this.projectId ?? '');
      });
  }

  openAssignToDialog(worksheetId: string): void {
    if (this.hasUnsavedChanges) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'moisture-worksheet',
        data: {
          title: 'Unsaved Changes',
          content: 'You have unsaved changes. Do you want to continue assigning without saving your changes?',
          cancelText: 'Cancel',
          confirmText: 'Continue'
        },
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'YES') {
          this.openAssignDialogInternal(worksheetId);
        }
      });
      return;
    }
    
    this.openAssignDialogInternal(worksheetId);
  }

  openAssignDialogInternal(worksheetId: string): void {
    this.dialog
      .open(AssignToLabtechComponent, {
        data: {
          worksheetId,
          type: 'moistureWorksheets',
          multipleSelected : this.isMultipleSelected(worksheetId),
          worksheetRows : this.selection.selected,
          totalNumOfWorksheets: this.dataSource.data.length,
          role: this.userRoles
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((assigned) => {
        if (assigned) {
          this.fetchWorksheet(this.projectId ?? '');
          this.rowDeletionSuccess.emit();
          this.assigned.emit(true);
          this.selection.clear();
        }
      });
  }

  findDateDifference(startDate: Date, endDate: Date) {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return Math.trunc(dayDifference);
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  setDepth(elem: any): string {
    let depthRange = '';
    if (elem.depth !== null) {
      depthRange = `${elem.depth}`;
    }
    if (elem.endDepth) {
      if (elem.depth !== null && elem.endDepth === null) {
        depthRange = `${elem.depth}`;
      }
      if (elem.endDepth !== null && elem.depth !== null) {
        depthRange = `${elem.depth}-${elem.endDepth}`;
      }
    }
    return depthRange;
  }

  private mapUser(worksheet: any): string | null {
    if (worksheet.assignedToId) {
      return `${worksheet.assignedTo.firstName} ${worksheet.assignedTo.lastName}`;
    }
    return null;
  }

  fetchWorksheet(projectId: string): void {
    this.nativeSoilService.getNativeSoilWorkSheet(projectId).subscribe({
      next: (response: any) => {
        let initial = 1;
        console.log(response, 'worksheet api response');
        this.worksheets = response?.moistureWorksheets ?? [];
        this.worksheets.forEach((worksheet: any) => {

          // Sort multiple moisture results by moisture content DESC
          if (worksheet.multipleMoistureResults) {
            worksheet.multipleMoistureResults.sort(
              (
                a: { moistureContent: any },
                b: { moistureContent: any }
              ) =>
                Number(b.moistureContent ?? 0) -
                Number(a.moistureContent ?? 0)
            );
          }

          if (!worksheet.groupOrder) {
            initial += 1;
          }
          worksheet.sequence = initial;

          if (worksheet.collectionDate) { 
            worksheet.collectionDate = DateTimeUtils.formatDateAndTime( worksheet.collectionDate, 'MM/dd/yyyy');
          }

          worksheet.displayDepth = this.setDepth(worksheet);
          worksheet.assignedTo = this.mapUser(worksheet);
        });

        // Sort worksheets to match Compaction worksheet order
        this.worksheets.sort((a: any, b: any) => {

          // 1. Sample ID
          const sampleCompare = String(a.sampleId ?? '').localeCompare(
            String(b.sampleId ?? ''),
            undefined,
            {
              numeric: true,
              sensitivity: 'base'
            }
          );
          if (sampleCompare !== 0) {
            return sampleCompare;
          }

          // 2. Depth
          const depthCompare = Number(a.depth ?? 0) - Number(b.depth ?? 0);
          if (depthCompare !== 0) {
            return depthCompare;
          }

          // 3. Native Soil Sample ID
          const nativeSampleCompare = String(a.nativeSoilSampleId ?? '').localeCompare(
              String(b.nativeSoilSampleId ?? ''),
              undefined,
              {
                numeric: true,
                sensitivity: 'base'
              }
            );
          if (nativeSampleCompare !== 0) {
            return nativeSampleCompare;
          }

          // 4. Compaction Effort
          const effortCompare = Number(a.compactionEffort ?? 0) - Number(b.compactionEffort ?? 0);
          if (effortCompare !== 0) {
            return effortCompare;
          }

          // 5. Moisture Content DESC
          const moistureCompare = Number(b.moistureContent ?? 0) - Number(a.moistureContent ?? 0);
          if (moistureCompare !== 0) {
            return moistureCompare;
          }

          return 0;
        });

        this.dataSource.data = this.worksheets;
        this.dataSource.data.forEach(e => this.updateCalculations(e, true));
        this.rowData = this.dataSource.data;
        this.originalWorksheetData = structuredClone(this.dataSource.data);
      },
      error: (err) => {
        console.error('Error fetching worksheets:', err);
      },
    });
  }
}
